import { useCallback, useMemo } from "react";
import { IdempotencyKey } from "@utils/idempotencyKey";
import { WSQueryCache } from "../../../query/helpers";
import {
  QUERY_EMPLOYEE_TAX_FORM_SCHEMA,
  QUERY_EMPLOYEE_TAX_FORM_SCHEMA_WITH_VALUES,
  QUERY_EMPLOYEE_TAX_FORMS_CHECKLIST
} from "../../../query/employeeTaxForms/keys";
import { useQueryEmployeeTaxFormSchemaWithValues } from "../../../query/employeeTaxForms/queries/useQueryEmployeeTaxFormSchemaWithValues";
import { getRequirementsDependencies } from "../../Requirements/dependencies";
import { employeeTaxFormsService } from "../../../services/employeeTaxForms";
import { DynamicFormDataAdapter } from "../../DynamicForm/types";
import { ISubmitFormRequest } from "../types";

// A submit recomputes the checklist, the schema, and the payee's requirement
// status server-side; refresh them all so completion shows without a page
// refresh (same dependency set as useMutationEmployeeTaxFormSubmit).
const invalidateSubmitDependencies = async () => {
  await WSQueryCache.invalidateQueries(QUERY_EMPLOYEE_TAX_FORMS_CHECKLIST);
  await WSQueryCache.invalidateQueries(QUERY_EMPLOYEE_TAX_FORM_SCHEMA);
  await WSQueryCache.invalidateQueries(
    QUERY_EMPLOYEE_TAX_FORM_SCHEMA_WITH_VALUES
  );
  for (const key of getRequirementsDependencies()) {
    await WSQueryCache.invalidateQueries(key);
  }
};

/**
 * The employee-tax-forms binding of the engine's DynamicFormDataAdapter: how a
 * tax form is submitted (with the V3 IdempotencyKey convention — one key per
 * logical request, stable across retries, destroyed on success so a later
 * intentional resubmit mints a fresh key), how in-progress values are
 * re-evaluated (the values-hash-cached schema query), and which tax/requirement
 * caches refresh on the engine's 409 outcomes.
 */
export const useEmployeeTaxFormsDataAdapter = (
  formInstanceId: string
): DynamicFormDataAdapter => {
  const idempotencyKey = useMemo(
    () => new IdempotencyKey({ formInstanceId }),
    [formInstanceId]
  );

  const submit = useCallback(
    async (request: ISubmitFormRequest) => {
      const response = await employeeTaxFormsService.submit(
        formInstanceId,
        request,
        idempotencyKey.value
      );
      idempotencyKey.destroy();
      await invalidateSubmitDependencies();
      return response;
    },
    [formInstanceId, idempotencyKey]
  );

  const useReEvaluation: DynamicFormDataAdapter["useReEvaluation"] =
    useCallback(
      ({ values, enabled }) => {
        // A custom hook supplied as data (see DynamicFormDataAdapter): the
        // engine calls it unconditionally on every render, and its own hook
        // composition is fixed, so the rules of hooks hold even though the
        // static analyzer can't see the call site.
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const query = useQueryEmployeeTaxFormSchemaWithValues(
          { formInstanceId, values },
          { enabled }
        );
        return { data: query.data, isFetching: query.isFetching };
      },
      [formInstanceId]
    );

  const onPrerequisiteNotMet = useCallback(async () => {
    await WSQueryCache.invalidateQueries(QUERY_EMPLOYEE_TAX_FORMS_CHECKLIST);
  }, []);

  // Same set as a successful submit: the reload must refresh the checklist and
  // requirement status alongside the schema, or stale statuses linger after
  // the revision bump.
  const onStaleRevisionReload = useCallback(invalidateSubmitDependencies, []);

  return useMemo(
    () => ({
      submit,
      useReEvaluation,
      onPrerequisiteNotMet,
      onStaleRevisionReload
    }),
    [submit, useReEvaluation, onPrerequisiteNotMet, onStaleRevisionReload]
  );
};
